import { Component, Input } from '@angular/core';
import { MinimalVerifyResponse } from '@app/shared/interfaces/tiered-verify';

/**
 * The MINIMAL-tier emergency card shown on the public break-glass verify pages
 * (MOLST + VA directive) when the scanning device is NOT registered: big
 * patient name, unmistakable code status, blockchain badge, health care agent
 * contact and integrity verdict — nothing else.
 */
@Component({
  selector: 'app-emergency-summary-card',
  templateUrl: './emergency-summary-card.component.html',
  styleUrls: ['./emergency-summary-card.component.sass'],
})
export class EmergencySummaryCardComponent {
  @Input() public summary: MinimalVerifyResponse | null = null;

  /**
   * True only when the backend positively says this document governs care now.
   * A missing documentState (older backend) is NOT treated as current — the
   * card warns instead, because presenting a stale DNR as live is the one
   * failure this screen must never produce.
   */
  public get isCurrent(): boolean {
    return this.summary?.documentState === 'current';
  }

  /** Headline for the banner shown above everything else when not current. */
  public get stateLabel(): string {
    switch (this.summary?.documentState) {
      case 'superseded':
        return 'REPLACED — a newer version of this document exists';
      case 'revoked':
        return 'REVOKED — the patient cancelled this document';
      case 'withdrawn':
        return 'WITHDRAWN — these orders were withdrawn';
      case 'not_yet_active':
        return 'NOT IN EFFECT — this document is not signed and active';
      default:
        return 'STATUS UNCONFIRMED — could not confirm this is the current document';
    }
  }

  /** What the responder should do about it, in one line. */
  public get stateHint(): string {
    switch (this.summary?.documentState) {
      case 'superseded':
      case 'revoked':
      case 'withdrawn':
        return 'Do not rely on the wishes below. Confirm the current document with the care team.';
      case 'not_yet_active':
        return 'These wishes are not yet in force. Confirm with the care team.';
      default:
        return 'Confirm with the care team before relying on the wishes below.';
    }
  }

  /** 'yes' | 'no' | 'unsure' | null, normalized defensively. */
  public get cpr(): 'yes' | 'no' | 'unsure' | null {
    const raw = this.summary && this.summary.codeStatus && this.summary.codeStatus.cpr;
    const value = (raw || '').toString().toLowerCase();
    return value === 'yes' || value === 'no' || value === 'unsure' ? (value as 'yes' | 'no' | 'unsure') : null;
  }

  public get cprLabel(): string {
    switch (this.cpr) {
      case 'yes':
        return 'YES — attempt CPR';
      case 'no':
        return 'NO — do not attempt CPR (DNR)';
      case 'unsure':
        return 'UNSURE';
      default:
        return 'Not specified';
    }
  }

  /**
   * Which document the code status came from, in words. Whoever reads this
   * card is standing over a patient — they should not have to decode an
   * internal identifier like "md_advance_directive".
   */
  public get sourceLabel(): string | null {
    const source = (this.summary?.codeStatus?.source || '').toString().toLowerCase();
    switch (source) {
      case 'molst':
        return 'Medical orders (MOLST), signed by a clinician';
      case 'md_advance_directive':
        return 'The patient’s Maryland advance directive';
      case 'va':
      case 'va_10_0137':
        return 'The patient’s VA advance directive';
      default:
        return source ? source.replace(/_/g, ' ') : null;
    }
  }

  public get shortTxId(): string | null {
    const txId = this.summary && this.summary.blockchain && this.summary.blockchain.txId;
    if (!txId) {
      return null;
    }
    return txId.length > 12 ? `${txId.slice(0, 12)}…` : txId;
  }
}
